import type { ReactElement } from 'react';

import { cn } from '@/components/ui/cn';
import { ExportMenu } from '@/components/ui/ExportMenu';
import { CardFrame, CardNote, QueryBody, RANGE_NOTE } from '@/components/ui/card-frame';

import {
  analysisQuery,
  AXIS_LABELS,
  deviationTone,
  formatHours,
  formatPct,
  rangeProblem,
} from '../presentation';
import { AXES, type Axis, type DeviationRow, type TimeStudyFilters } from '../types';
import { useDeviation } from '../use-time-study';

// Desviación por eje (so=420, spec §4.2 tarjeta 5; mockup 05 "Analítica de desviación"):
// persona, tipo de documento, proceso o departamento (según los puestos de cada persona en
// el LMS; reunión 2026-10-27); filas con esfuerzo, estándar y desviación, y los trabajos
// sin estándar aparte.

interface Props {
  filters: TimeStudyFilters;
  companyId: string;
  onAxisChange: (axis: Axis) => void;
}

/** Texto de la fila sin valor en el eje: sin persona o sin departamento conocido. */
const NONE: Readonly<Record<Axis, string>> = {
  person: 'Sin persona asignada',
  documentType: 'Sin nombre',
  processArea: 'Sin nombre',
  department: 'Sin departamento',
};

/** Fila del eje departamento con las etapas que nadie midió (contrato con qms-api). */
const UNASSIGNED_DEPARTMENT = 'unassigned';

function labelOf(r: DeviationRow, axis: Axis): string {
  const named = r.keys[axis];
  if (named === null || named === undefined) {
    return NONE[axis];
  }
  if (axis === 'department' && named.id === UNASSIGNED_DEPARTMENT) {
    return NONE.person;
  }
  return named.name ?? (axis === 'person' ? 'Usuario sin nombre' : 'Sin nombre');
}

function keyOf(r: DeviationRow, axis: Axis): string {
  return r.keys[axis]?.id ?? 'none';
}

function Measured({ rows, axis }: { rows: DeviationRow[]; axis: Axis }): ReactElement {
  const scale = Math.max(1, ...rows.map((r) => Math.abs(r.deviationPct ?? 0)));
  return (
    <ul className="mx" aria-label="Con estándar">
      {rows.map((r) => {
        const tone = deviationTone(r.deviationPct);
        return (
          <li key={keyOf(r, axis)} className="mrow">
            <span className="mlbl">{labelOf(r, axis)}</span>
            <div className="mdata">
              <span
                className={cn('mbar', tone)}
                aria-hidden="true"
                style={{ width: `${(Math.abs(r.deviationPct ?? 0) / scale) * 100}%` }}
              />
              <span className="mval">
                {formatHours(r.effortWithStandardSeconds)} de {formatHours(r.standardSeconds ?? 0)}{' '}
                · <b className={tone}>{formatPct(r.deviationPct)}</b>
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function AxisDeviationCard({ filters, companyId, onAxisChange }: Props): ReactElement {
  const axis = filters.eje;
  const problem = rangeProblem(filters);
  const query = useDeviation(axis, filters, { companyId, enabled: problem === null });
  return (
    <CardFrame
      title="Desviación por eje"
      count="real frente a estándar"
      actions={
        problem === null && (
          <ExportMenu
            compact
            path="/api/v1/quality/productivity/deviation/export"
            query={`groupBy=${axis}&${analysisQuery(filters)}`}
            title="Desviación por eje"
          />
        )
      }
    >
      <div className="cb" style={{ paddingBottom: 0 }}>
        <div className="seg" role="group" aria-label="Agrupar por">
          {AXES.map((a) => (
            <button
              key={a}
              type="button"
              aria-pressed={a === axis}
              className={cn(a === axis && 'on')}
              onClick={() => onAxisChange(a)}
            >
              {AXIS_LABELS[a]}
            </button>
          ))}
        </div>
      </div>
      {problem !== null ? (
        <CardNote>{RANGE_NOTE}</CardNote>
      ) : (
        <QueryBody
          query={query}
          isEmpty={(d) => d.rows.length === 0}
          emptyText="No hay trabajos terminados en el rango."
        >
          {(d) => {
            const measured = d.rows.filter((r) => r.standardSeconds !== null);
            const without = d.rows.filter((r) => r.standardSeconds === null);
            return (
              <div className="cb">
                {measured.length > 0 && <Measured rows={measured} axis={axis} />}
                {without.length > 0 && (
                  <>
                    <div className="eyebrow mxhead">Sin estándar</div>
                    <ul className="mx" aria-label="Sin estándar">
                      {without.map((r) => (
                        <li key={keyOf(r, axis)} className="mrow">
                          <span className="mlbl">{labelOf(r, axis)}</span>
                          <span className="mval">{formatHours(r.effortSeconds)}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            );
          }}
        </QueryBody>
      )}
    </CardFrame>
  );
}
